import React from 'react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { 
  ChevronDown, 
  ChevronRight,
  AlertTriangle,
  AlertCircle,
  Info,
  ArrowRightCircle,
  ArrowLeftCircle,
  Database
} from 'lucide-react';
import { EnhancedLogEntry, formatTimestampGMT3 } from '@/utils/enhancedLogging';

interface AppLogPanelProps {
  logs: EnhancedLogEntry[];
  queryVersion: string;
}

export const AppLogPanel: React.FC<AppLogPanelProps> = ({ logs, queryVersion }) => {
  const [expandedLogs, setExpandedLogs] = React.useState<Record<string, boolean>>({});

  const toggleLogExpansion = (logId: string) => {
    setExpandedLogs(prev => ({
      ...prev,
      [logId]: !prev[logId]
    }));
  };

  const getLogIcon = (level: string) => {
    switch (level) {
      case 'error':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      case 'query':
        return <Database className="h-4 w-4 text-blue-500" />;
      case 'entry':
        return <ArrowRightCircle className="h-4 w-4 text-green-500" />;
      case 'exit':
        return <ArrowLeftCircle className="h-4 w-4 text-purple-500" />;
      default:
        return <Info className="h-4 w-4 text-gray-500" />;
    }
  };

  const getLogBadge = (level: string) => {
    switch (level) {
      case 'error':
        return <Badge variant="error">Error</Badge>;
      case 'warning':
        return <Badge variant="destructive">Warning</Badge>;
      case 'query':
        return <Badge className="bg-blue-500">Query</Badge>;
      case 'entry':
        return <Badge className="bg-green-500">Entry</Badge>;
      case 'exit':
        return <Badge className="bg-purple-500">Exit</Badge>;
      default:
        return <Badge variant="outline">Info</Badge>;
    }
  };

  const getSourceBadge = (source?: string) => {
    if (!source) return null;
    
    switch (source) {
      case 'serviceOperations:94':
        return <Badge className="bg-red-700 ml-2">Service Ops #94</Badge>;
      case 'serviceOperations:131':
        return <Badge className="bg-amber-600 ml-2">Service Ops #131</Badge>;
      case 'useServicesQuery:215':
        return <Badge className="bg-pink-600 ml-2">Query #215</Badge>;
      default:
        return <Badge variant="outline" className="ml-2">{source}</Badge>;
    }
  };

  const getLogRowStyle = (log: EnhancedLogEntry) => {
    let baseStyle = "border-l-4 pl-3 py-2 pr-3 hover:bg-gray-50";
    
    switch (log.level) {
      case 'error':
        return `${baseStyle} border-l-red-500 bg-red-50`;
      case 'warning':
        return `${baseStyle} border-l-yellow-500 bg-yellow-50`;
      case 'query':
        return `${baseStyle} border-l-blue-500 bg-blue-50`;
      case 'entry':
        return `${baseStyle} border-l-green-500 bg-green-50`;
      case 'exit':
        return `${baseStyle} border-l-purple-500 bg-purple-50`;
      default:
        return `${baseStyle} border-l-gray-500`;
    }
  };

  return (
    <>
      <div className="bg-gray-100 p-2 rounded-t-md border flex justify-between items-center">
        <div>
          <h3 className="font-medium">App Log</h3>
          <div className="text-xs text-gray-500">
            Showing {logs.length} log entries
          </div>
        </div>
        <Badge className="bg-indigo-600">QUERY_VERSION: {queryVersion}</Badge>
      </div>
      <ScrollArea className="h-[400px] border-x border-b rounded-b-md">
        <div className="space-y-1 p-1">
          {logs.length === 0 ? (
            <div className="text-gray-400 p-4 text-center">No logs captured yet</div>
          ) : (
            logs.map((log, index) => {
              const logId = `${log.entryId || ''}-${index}`;
              const isExpanded = expandedLogs[logId] || false;
              const hasDetails = log.details && Object.keys(log.details).length > 0;
              
              return (
                <div 
                  key={index} 
                  className={getLogRowStyle(log)}
                >
                  <div
                    className={`flex items-start justify-between 
                      ${hasDetails ? 'cursor-pointer' : ''}`}
                    onClick={hasDetails ? () => toggleLogExpansion(logId) : undefined}
                  >
                    <div className="flex items-center">
                      {getLogIcon(log.level)}
                      <span className="text-xs text-gray-600 ml-2">{formatTimestampGMT3(log.timestamp)}</span>
                      <div className="ml-2 flex items-center">
                        {getLogBadge(log.level)}
                        {getSourceBadge(log.source)}
                      </div>
                    </div>
                    
                    {hasDetails && (
                      <button className="text-gray-500">
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" />
                        ) : (
                          <ChevronRight className="h-4 w-4" />
                        )}
                      </button>
                    )}
                  </div>
                  
                  <div className="mt-1 text-sm">{log.message}</div>
                  
                  {isExpanded && log.details && (
                    <div className="mt-2 text-xs p-2 bg-gray-100 rounded border overflow-auto">
                      <pre className="whitespace-pre-wrap break-all">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </ScrollArea>
    </>
  );
};

export default AppLogPanel;
